// Agendamiento · Reservas
//
// Las reservas de un rango de días, con búsqueda por código, RUT, correo o nombre
// (la búsqueda ignora el rango: sirve para encontrar a alguien que llama sin saber
// la fecha). Las tarjetas filtran; "Por revisar" junta pagos que Transbank no ha
// resuelto, boletas por verificar en Bsale y pagos aprobados que quedaron sin hora.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DatePicker, Dropdown, Input, Select, Table, Tooltip } from 'antd';
import { CalendarOutlined, CloseCircleOutlined, EyeOutlined, MailOutlined } from '@ant-design/icons';
import {
  FiAlertTriangle, FiCalendar, FiCheckCircle, FiClock, FiDownload, FiLayers, FiMoreVertical, FiSearch,
} from 'react-icons/fi';
import dayjs from 'dayjs';
import Agenda from '../../../../service/Agenda';
import { mensajeDeError } from '../../../../service/client';
import { useNotify } from '../../../../providers/NotificationProvider';
import useAuthContext from '../../../../hooks/useAuthContext';
import { can } from '../../../../config/rbac';
import { exportarHojaUnica } from '../../../../utils/exportExcel';
import { EncabezadoAdmin, TarjetaResumen, ChipNeutro, Iniciales, claseBtnSecundario } from '../../../Ui/Admin/PiezasAdmin';
import { BarraFiltros, Chip, Vacio, chipDe, claseBtnKebab } from './piezas';
import DetalleReserva from './DetalleReserva';
import { BOLETA, ESTADOS, RESULTADO_PAGO, fecha, fechaCorta, hora, pesos } from './util';

const { RangePicker } = DatePicker;

const VISTAS = {
  todas: () => true,
  confirmadas: (c) => c.estado === 'confirmada',
  en_curso: (c) => c.estado === 'retenida' || c.estado === 'pagando',
};

export default function Reservas() {
  const notify = useNotify();
  const { user } = useAuthContext();
  const puedeEditar = can(user, 'agenda', 'editar');

  const [rango, setRango] = useState([dayjs(), dayjs().add(6, 'day')]);
  const [agenda, setAgenda] = useState('todas');
  const [busqueda, setBusqueda] = useState('');
  const [q, setQ] = useState('');
  const [vista, setVista] = useState('todas'); // todas | confirmadas | en_curso | revision
  const [agendas, setAgendas] = useState([]);
  const [citas, setCitas] = useState([]);
  const [porRevisar, setPorRevisar] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [abierta, setAbierta] = useState(null); // { id, accion? }

  useEffect(() => { Agenda.agendas().then(setAgendas).catch(() => {}); }, []);

  // La búsqueda espera a que se deje de escribir, igual que en Recintos.
  useEffect(() => {
    const t = setTimeout(() => setQ(busqueda.trim()), 300);
    return () => clearTimeout(t);
  }, [busqueda]);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const params = {
        desde: rango?.[0]?.format('YYYY-MM-DD'),
        hasta: rango?.[1]?.format('YYYY-MM-DD'),
        agenda: agenda !== 'todas' ? agenda : undefined,
        q: q || undefined,
      };
      const [lista, revision] = await Promise.all([Agenda.citas(params), Agenda.citas({ revision: '1' })]);
      setCitas(lista);
      setPorRevisar(revision);
    } catch (err) {
      notify.error(mensajeDeError(err, 'No se pudieron cargar las reservas'));
    } finally {
      setCargando(false);
    }
  }, [rango, agenda, q, notify]);

  useEffect(() => { cargar(); }, [cargar]);

  const visibles = useMemo(
    () => (vista === 'revision' ? porRevisar : citas.filter(VISTAS[vista])),
    [vista, citas, porRevisar],
  );
  const conteo = useMemo(() => ({
    todas: citas.length,
    confirmadas: citas.filter(VISTAS.confirmadas).length,
    en_curso: citas.filter(VISTAS.en_curso).length,
  }), [citas]);

  const hayFiltros = busqueda || agenda !== 'todas' || vista !== 'todas';
  const limpiar = () => { setBusqueda(''); setAgenda('todas'); setVista('todas'); };

  const exportar = async () => {
    try {
      if (!visibles.length) { notify.warning('No hay reservas para exportar'); return; }
      await exportarHojaUnica({
        archivo: 'reservas-servicios',
        titulo: 'Reservas de servicios',
        subtitulo: 'Agendamiento: reservas compradas desde el sitio',
        usuario: user,
        filtros: {
          'Desde': rango?.[0]?.format('DD/MM/YYYY'),
          'Hasta': rango?.[1]?.format('DD/MM/YYYY'),
          'Búsqueda': q || null,
          'Profesional o box': agendas.find(a => a._id === agenda)?.nombre || 'Todos',
        },
        nombreHoja: 'Reservas',
        columnas: [
          { header: 'Fecha', key: 'fecha', tipo: 'texto' },
          { header: 'Hora', key: 'hora', tipo: 'texto' },
          { header: 'Servicio', key: 'servicio', tipo: 'texto', ancho: 28 },
          { header: 'Atiende', key: 'agenda', tipo: 'texto', ancho: 26 },
          { header: 'Cliente', key: 'cliente', tipo: 'texto', ancho: 28 },
          { header: 'RUT', key: 'rut', tipo: 'texto' },
          { header: 'Correo', key: 'email', tipo: 'texto', ancho: 28 },
          { header: 'Teléfono', key: 'telefono', tipo: 'texto' },
          { header: 'Estado', key: 'estado', tipo: 'texto' },
          { header: 'Monto pagado', key: 'monto', tipo: 'moneda', total: true },
          { header: 'Boleta', key: 'boleta', tipo: 'texto' },
          { header: 'Código', key: 'codigo', tipo: 'texto' },
        ],
        filas: visibles.map(c => ({
          fecha: c.inicio ? fechaCorta(c.inicio) : 'Sin hora',
          hora: c.inicio ? `${hora(c.inicio)}–${hora(c.fin)}` : '',
          servicio: c.servicioNombre,
          agenda: c.agendaNombre,
          cliente: c.cliente?.nombre,
          rut: c.cliente?.rut,
          email: c.cliente?.email,
          telefono: c.cliente?.telefono,
          estado: ESTADOS[c.estado]?.[1] || c.estado,
          monto: c.pago?.resultado === 'aprobado' ? c.pago.monto : null,
          boleta: c.facturacion?.documento?.numero ? `N° ${c.facturacion.documento.numero}` : (BOLETA[c.facturacion?.estado]?.[1] || ''),
          codigo: c.codigo,
        })),
      });
    } catch { notify.error('Error al generar Excel'); }
  };

  const columnas = [
    {
      title: 'Reserva', key: 'reserva', width: 210,
      render: (_, c) => (
        <div className="flex items-center gap-3 min-w-0">
          <span className={`w-9 h-9 flex-shrink-0 rounded-xl flex items-center justify-center
            ${c.estado === 'confirmada' ? 'bg-blue-50 text-primary' : 'bg-gray-100 text-gray-400'}`}>
            <FiCalendar size={16} />
          </span>
          <div className="min-w-0">
            {c.inicio ? (
              <>
                <div className="font-bold text-gray-900 tabular-nums">{hora(c.inicio)}–{hora(c.fin)}</div>
                <div className="text-xs text-gray-500 capitalize">{fecha(c.inicio)}</div>
              </>
            ) : (
              <>
                <div className="font-bold text-[#92400e]">Sin hora</div>
                <div className="text-xs text-gray-500">Debe elegir otra</div>
              </>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Cliente', key: 'cliente',
      render: (_, c) => (
        <div className="flex items-center gap-3 min-w-0">
          <Iniciales nombre={c.cliente?.nombre} />
          <div className="min-w-0">
            <div className="font-bold text-gray-900 truncate">{c.cliente?.nombre}</div>
            <div className="text-xs text-gray-500 truncate">{c.cliente?.rut} · {c.cliente?.telefono}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Servicio', key: 'servicio', width: 230,
      render: (_, c) => (
        <div className="min-w-0">
          <div className="text-sm font-semibold text-gray-900 truncate">{c.servicioNombre}</div>
          <div className="text-xs text-gray-500 truncate">{c.agendaNombre}</div>
        </div>
      ),
    },
    {
      title: 'Estado', key: 'estado', width: 190,
      render: (_, c) => (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          {chipDe(ESTADOS, c.estado)}
          {c.asistencia && <Chip tono={c.asistencia === 'asistio' ? 'azul' : 'rojo'}>{c.asistencia === 'asistio' ? 'Asistió' : 'No asistió'}</Chip>}
          {c.requiereRevision && (
            <Tooltip title={c.motivoRevision}><FiAlertTriangle size={15} className="text-[#b45309] cursor-help" aria-label="Requiere revisión" /></Tooltip>
          )}
        </span>
      ),
    },
    {
      title: 'Pago', key: 'pago', width: 120, align: 'right',
      render: (_, c) => c.pago?.resultado === 'aprobado'
        ? <span className="font-bold text-gray-900 tabular-nums">{pesos(c.pago.monto)}</span>
        : <span className="text-xs text-gray-400">{RESULTADO_PAGO[c.pago?.resultado] || (c.estado === 'pagando' ? 'En curso' : '—')}</span>,
    },
    {
      title: 'Boleta', key: 'boleta', width: 150,
      render: (_, c) => c.facturacion?.documento?.numero
        ? <ChipNeutro>N° {c.facturacion.documento.numero}</ChipNeutro>
        : (c.facturacion?.estado && c.facturacion.estado !== 'no_aplica' ? chipDe(BOLETA, c.facturacion.estado) : <span className="text-gray-300">—</span>),
    },
    {
      title: <span className="sr-only">Acciones</span>, key: 'acciones', width: 64, align: 'center',
      render: (_, c) => {
        const pagada = c.estado === 'confirmada' || c.estado === 'pagada_sin_hora';
        const items = [
          { key: 'ver', icon: <EyeOutlined />, label: 'Ver detalle', onClick: () => setAbierta({ id: c._id }) },
          ...(puedeEditar && pagada ? [
            { key: 'hora', icon: <CalendarOutlined />, label: c.estado === 'pagada_sin_hora' ? 'Elegir hora' : 'Cambiar hora', disabled: Boolean(c.asistencia), onClick: () => setAbierta({ id: c._id, accion: 'hora' }) },
            { key: 'correo', icon: <MailOutlined />, label: 'Reenviar correo', onClick: () => setAbierta({ id: c._id, accion: 'correo' }) },
            { type: 'divider' },
            { key: 'cancelar', icon: <CloseCircleOutlined />, danger: true, label: 'Cancelar reserva', disabled: Boolean(c.asistencia), onClick: () => setAbierta({ id: c._id, accion: 'cancelar' }) },
          ] : []),
        ];
        return (
          <span onClick={e => e.stopPropagation()}>
            <Dropdown trigger={['click']} placement="bottomRight" menu={{ items }}>
              <button type="button" aria-label={`Acciones de la reserva ${c.codigo}`} className={claseBtnKebab}>
                <FiMoreVertical size={17} />
              </button>
            </Dropdown>
          </span>
        );
      },
    },
  ];

  const desde = rango?.[0]?.format('DD/MM');
  const hasta = rango?.[1]?.format('DD/MM');

  return (
    <div>
      <EncabezadoAdmin
        titulo="Reservas de"
        destacado="servicios"
        subtitulo={q
          ? `Buscando “${q}” en todas las fechas`
          : `${conteo.todas} reservas del ${desde} al ${hasta} · ${conteo.confirmadas} confirmadas`}
      >
        <button type="button" onClick={exportar} disabled={!visibles.length} className={claseBtnSecundario}>
          <FiDownload size={15} /> Exportar Excel
        </button>
      </EncabezadoAdmin>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <TarjetaResumen icon={<FiLayers size={19} />} label="Todas" value={conteo.todas}
          activa={vista === 'todas'} onClick={() => setVista('todas')} />
        <TarjetaResumen icon={<FiCheckCircle size={19} />} label="Confirmadas" value={conteo.confirmadas}
          activa={vista === 'confirmadas'} onClick={() => setVista('confirmadas')} />
        <TarjetaResumen icon={<FiClock size={19} />} label="Retenidas o en pago" value={conteo.en_curso}
          activa={vista === 'en_curso'} onClick={() => setVista('en_curso')} />
        <TarjetaResumen icon={<FiAlertTriangle size={19} />} label="Por revisar (todas las fechas)" value={porRevisar.length}
          activa={vista === 'revision'} onClick={() => setVista('revision')} />
      </div>

      <BarraFiltros>
        <Input
          allowClear prefix={<FiSearch className="text-gray-400" />}
          placeholder="Buscar por código, RUT, correo o nombre…"
          value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
          className="!rounded-full" style={{ maxWidth: 340 }} aria-label="Buscar reservas"
        />
        <RangePicker value={rango} onChange={(v) => v && setRango(v)} allowClear={false} format="DD/MM/YYYY"
          disabled={Boolean(q) || vista === 'revision'} aria-label="Rango de fechas" />
        <Select value={agenda} onChange={setAgenda} style={{ minWidth: 220 }} aria-label="Profesional o box"
          disabled={vista === 'revision'}
          options={[{ value: 'todas', label: 'Todos los profesionales y boxes' }, ...agendas.map(a => ({ value: a._id, label: a.nombre }))]} />
        {hayFiltros && (
          <button type="button" onClick={limpiar} className="text-sm font-semibold text-gray-500 hover:text-primary">
            Limpiar filtros
          </button>
        )}
        <span className="ml-auto text-xs font-semibold text-gray-500">
          {visibles.length.toLocaleString('es-CL')} resultado{visibles.length !== 1 ? 's' : ''}
        </span>
      </BarraFiltros>

      <div className="admin-table-card">
        <Table
          columns={columnas}
          dataSource={visibles}
          loading={cargando}
          rowKey="_id"
          sticky
          rowClassName={() => 'fila-clicable'}
          onRow={(c) => ({ onClick: () => setAbierta({ id: c._id }) })}
          pagination={visibles.length > 20 ? { pageSize: 20, style: { padding: '12px 16px' }, showTotal: (t, r) => `${r[0]}-${r[1]} de ${t} reservas` } : false}
          scroll={{ x: 1000 }}
          locale={{
            emptyText: vista === 'revision'
              ? <Vacio titulo="Nada por revisar" texto="Todos los pagos y boletas están en orden." />
              : <Vacio titulo="No hay reservas con estos filtros" texto="Prueba con otro rango de fechas o limpia los filtros." />,
          }}
        />
      </div>

      <details className="mt-5 rounded-2xl bg-white border border-gray-100 p-4">
        <summary className="cursor-pointer text-sm font-bold text-gray-700 list-none flex items-center gap-2">
          <FiAlertTriangle size={15} className="text-primary" /> Cómo funcionan las reservas
        </summary>
        <ul className="mt-3 mb-0 pl-5 space-y-1.5 text-sm text-gray-600">
          <li>La persona elige la hora en nunoadeportes.cl y queda <strong>retenida</strong> por 10 minutos mientras paga.</li>
          <li>Mientras está <strong>en pago</strong> la hora no se libera: el sistema espera la respuesta de Transbank, y si la persona cierra el navegador le pregunta solo cada pocos minutos.</li>
          <li>Si el pago se aprueba pero la hora ya se ocupó, la reserva queda <strong>pagada sin hora</strong>: la persona elige otra con el link de su correo, o se le asigna desde aquí.</li>
          <li>Cancelar libera la hora pero <strong>no devuelve el pago</strong> ni emite nota de crédito: si corresponde devolución, la gestiona finanzas.</li>
        </ul>
      </details>

      <DetalleReserva
        citaId={abierta?.id} accionInicial={abierta?.accion}
        onClose={() => setAbierta(null)} onCambio={cargar}
      />
    </div>
  );
}
